import { normalizeName, normalizeAddress } from './normalization';
import { ExtractedContract } from '../types/contract';
import { SessionBuilding } from '../types/contract';

/**
 * レーベンシュタイン距離を計算
 * バックエンドと同じアルゴリズム
 */
const levenshteinDistance = (a: string, b: string): number => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix: number[][] = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[b.length][a.length];
};

/**
 * sessionConfirmedData から最も適切な建物を1件返す
 * バックエンドの find_best_matching_building と同じロジック
 *
 * 絞り込みフロー:
 * 1. 物件名完全一致で候補を列挙 → なければ住所完全一致で候補を列挙
 * 2. 候補が複数ある場合、家主名完全一致で絞り込み
 * 3. 一致があれば確定
 * 4. なければ家主カナ完全一致で絞り込み
 * 5. それでも複数の場合、
 *    min(家主名のレーベンシュタイン距離, 家主カナのレーベンシュタイン距離)で最小のものを返す
 *
 * @returns [building, match_type] or [null, null]
 */
export const findBestMatchingBuilding = (
  contract: ExtractedContract,
  sessionBuildings: SessionBuilding[],
  excludeContractId?: number
): [SessionBuilding | null, 'both' | 'name' | 'address' | null] => {
  const extractedName = contract.property_name || '';
  const extractedAddress = contract.property_address?.value || '';
  const extractedOwnerName = contract.owner_name?.value || '';
  const extractedOwnerKana = contract.owner_name_kana?.value || '';

  // 正規化
  const normalizedExtractedName = normalizeName(extractedName);
  const normalizedExtractedAddress = normalizeAddress(extractedAddress);
  const normalizedExtractedOwnerName = normalizeName(extractedOwnerName);

  // 自分自身を除外
  const candidates = sessionBuildings.filter(
    b => excludeContractId === undefined || b.sourceContractId !== excludeContractId
  );

  if (candidates.length === 0) {
    return [null, null];
  }

  // ステップ1: 物件名で検索
  let matches = candidates.filter(
    b => b.name_normalized === normalizedExtractedName
  );

  let matchType: 'both' | 'name' | 'address' | null = matches.length > 0 ? 'name' : null;

  // 物件名で見つからなければ住所で検索
  if (matches.length === 0 && extractedAddress) {
    const normalizedAddress = normalizeAddress(extractedAddress);
    matches = candidates.filter(
      b => normalizeAddress(b.address1) === normalizedAddress
    );
    matchType = matches.length > 0 ? 'address' : null;
  }

  // 候補が0件
  if (matches.length === 0) {
    return [null, null];
  }

  // 候補が1件
  if (matches.length === 1) {
    const building = matches[0];
    // 物件名と住所の両方が一致しているか確認
    if (
      building.name_normalized === normalizedExtractedName &&
      normalizeAddress(building.address1) === normalizedExtractedAddress
    ) {
      return [building, 'both'];
    }
    return [building, matchType];
  }

  console.log(`[FIND_BEST_BUILDING] Found ${matches.length} candidates`);

  // ステップ2: 家主名完全一致で絞り込み
  if (extractedOwnerName) {
    const exactNameMatch = matches.filter(
      b => b.owner.name_normalized === normalizedExtractedOwnerName
    );

    if (exactNameMatch.length > 0) {
      console.log(`[FIND_BEST_BUILDING] Found ${exactNameMatch.length} exact name matches`);
      const building = exactNameMatch[0];
      // 物件名と住所の両方が一致しているか確認
      if (
        building.name_normalized === normalizedExtractedName &&
        normalizeAddress(building.address1) === normalizedExtractedAddress
      ) {
        return [building, 'both'];
      }
      return [building, matchType];
    }
  }

  // ステップ3: 家主カナ完全一致で絞り込み
  if (extractedOwnerKana) {
    // カナは空白を削除して比較
    const normalizedKana = extractedOwnerKana.replace(/\s+/g, '');
    const exactKanaMatch = matches.filter(
      b => b.owner.name_kana?.replace(/\s+/g, '') === normalizedKana
    );

    if (exactKanaMatch.length > 0) {
      console.log(`[FIND_BEST_BUILDING] Found ${exactKanaMatch.length} exact kana matches`);
      const building = exactKanaMatch[0];
      // 物件名と住所の両方が一致しているか確認
      if (
        building.name_normalized === normalizedExtractedName &&
        normalizeAddress(building.address1) === normalizedExtractedAddress
      ) {
        return [building, 'both'];
      }
      return [building, matchType];
    }
  }

  // ステップ4: レーベンシュタイン距離で最も近いものを選ぶ
  console.log(`[FIND_BEST_BUILDING] Using Levenshtein distance to select best match`);

  let bestBuilding: SessionBuilding | null = null;
  let minDistance = Infinity;

  for (const building of matches) {
    const ownerName = building.owner.name || '';
    const ownerKana = building.owner.name_kana || '';

    // 名前とカナの距離を計算し、最小の方を使う
    let distance = Infinity;

    if (extractedOwnerName && ownerName) {
      distance = Math.min(
        distance,
        levenshteinDistance(normalizedExtractedOwnerName, normalizeName(ownerName))
      );
    }

    if (extractedOwnerKana && ownerKana) {
      distance = Math.min(
        distance,
        levenshteinDistance(
          extractedOwnerKana.replace(/\s+/g, ''),
          ownerKana.replace(/\s+/g, '')
        )
      );
    }

    if (distance < minDistance) {
      minDistance = distance;
      bestBuilding = building;
    }
  }

  if (bestBuilding) {
    // 物件名と住所の両方が一致しているか確認
    if (
      bestBuilding.name_normalized === normalizedExtractedName &&
      normalizeAddress(bestBuilding.address1) === normalizedExtractedAddress
    ) {
      return [bestBuilding, 'both'];
    }
    return [bestBuilding, matchType];
  }

  return [null, null];
};
